import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { Footer } from '../../components/Footer';
import { NotificationBell } from '../../components/NotificationBell';
import { BetaBanner } from '../../components/BetaBanner';
import { SandboxBanner } from '../../components/SandboxBanner';
import { SyncBanner } from '../../components/SyncBanner';
import { TrialPaywall } from '../../components/TrialPaywall';
import { BillingAccessBanner } from '../../components/BillingAccessBanner';
import { MfaNudge } from '../../components/security/MfaNudge';
import { UserMenu } from '../../components/UserMenu';
import { useAuthStore } from '../../store/auth';
import { GuidedTour } from '../tour/GuidedTour';
import { Sidebar, type NavItem } from './Sidebar';

/** Authenticated application shell — left nav rail + slim top bar (Frontend §Application Shell). */
export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const canManageOrg = user?.role ? roleHasPermissions(user.role, [Permission.OrgManage]) : false;
  // The sandbox is a read-only demo: hide Members + Settings (the whole account/
  // settings area) so a demo user can't reach connect/billing/member management.
  const isSandbox = user?.organizationKind === 'SANDBOX';

  const navItems: NavItem[] = [
    { to: '/', label: 'Dashboard', end: true, icon: 'dashboard' },
    { to: '/domains', label: 'Domains', icon: 'domains' },
    ...(isSandbox ? [] : ([{ to: '/members', label: 'Members', icon: 'members' }] as NavItem[])),
    ...(canManageOrg && !isSandbox
      ? ([
          { to: '/activity', label: 'Activity', icon: 'activity' },
          // Settings is a section: its sub-nav (Organization Settings, Billing) is
          // surfaced by the Sidebar when the user is in it. Billing is no longer a
          // top-level item — it lives under Settings, admin-only.
          { to: '/settings', label: 'Settings', icon: 'settings' },
        ] as NavItem[])
      : []),
    // Visible to every role.
    { to: '/help', label: 'Help & Feedback', icon: 'help' },
  ];

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen">
      {/* Day-8 trial paywall — blocks the whole app once the trial lapses with no
          card (never in beta / sandbox). Rendered first so it overlays everything. */}
      <TrialPaywall />

      {/* Desktop rail */}
      <Sidebar items={navItems} className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen" />

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-obsidian/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <div className="absolute inset-y-0 left-0 shadow-2xl">
            <Sidebar items={navItems} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        {/* Beta + sandbox banners + top bar anchored together as one sticky unit. */}
        <div className="sticky top-0 z-30">
          <BetaBanner />
          <SandboxBanner />
          <header
            className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-2.5 backdrop-blur lg:px-6"
            style={{ backgroundColor: 'color-mix(in srgb, var(--obsidian) 82%, transparent)' }}
          >
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
                className="rounded-md p-1.5 text-haze transition-colors hover:bg-surface-2 hover:text-bone lg:hidden"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
              </button>
              {user?.organizationName && (
                <span className="truncate text-sm font-medium text-haze">
                  {user.organizationName}
                </span>
              )}
            </div>
            <nav className="flex items-center gap-2 text-sm">
              <NotificationBell />
              {user && <UserMenu user={user} onSignOut={onLogout} />}
            </nav>
          </header>
        </div>

        <SyncBanner />

        <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-8 lg:px-8">
          <MfaNudge />
          <BillingAccessBanner />
          <Outlet />
        </main>
        <Footer />
      </div>
      <GuidedTour />
    </div>
  );
}
